import heic2any from "heic2any";

const withTimeout = (promise, ms, label = "Operation") => {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error(`${label} timed out after ${ms}ms`)),
      ms
    );
  });
  return Promise.race([promise, timeoutPromise]).finally(() =>
    clearTimeout(timeoutId)
  );
};

const _generateImageThumbnail = async (file, maxDimension = 200, quality = 0.8) => {
    let targetBlob = file;
    const ext = (file.name || "").split(".").pop().toLowerCase();

    if (ext === "heic" || ext === "heif" || file.type === "image/heic" || file.type === "image/heif") {
        try {
            const conversionResult = await heic2any({
                blob: file,
                toType: "image/jpeg",
                quality: 0.8
            });
            targetBlob = Array.isArray(conversionResult) ? conversionResult[0] : conversionResult;
        } catch (convErr) {
            console.error("HEIC conversion failed for thumbnail:", convErr);
            throw convErr;
        }
    }

    return new Promise((resolve, reject) => {
        const imageUrl = URL.createObjectURL(targetBlob);
        const img = new Image();

        img.onload = () => {
            try {
                const scale = Math.min(1, maxDimension / Math.max(img.width, img.height));
                const targetWidth = Math.max(1, Math.round(img.width * scale));
                const targetHeight = Math.max(1, Math.round(img.height * scale));

                const canvas = document.createElement("canvas");
                canvas.width = targetWidth;
                canvas.height = targetHeight;
                const ctx = canvas.getContext("2d");

                if (!ctx) {
                    URL.revokeObjectURL(imageUrl);
                    return reject(new Error("Canvas not supported"));
                }

                ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

                canvas.toBlob(
                    (blob) => {
                        URL.revokeObjectURL(imageUrl);
                        if (!blob) return reject(new Error("Failed to create thumbnail"));
                        resolve(blob);
                    },
                    "image/jpeg",
                    quality
                );
            } catch (err) {
                URL.revokeObjectURL(imageUrl);
                reject(err);
            }
        };

        img.onerror = () => {
            URL.revokeObjectURL(imageUrl);
            reject(new Error("Failed to load image"));
        };

        img.src = imageUrl;
    });
};

export const generateImageThumbnail = (file, maxDimension = 200, quality = 0.8) => {
  return withTimeout(
    _generateImageThumbnail(file, maxDimension, quality),
    10000,
    "Image thumbnail"
  );
};

const _generateVideoThumbnail = (file, seekTo = 1.0, maxDimension = 200, quality = 0.8) => {
    return new Promise((resolve, reject) => {
        const video = document.createElement("video");
        const videoUrl = URL.createObjectURL(file);
        let isSeeking = false;

        video.autoplay = false;
        video.muted = true;
        video.playsInline = true;

        video.onloadedmetadata = () => {
            // Seek to the specified time, or half the video length if it's shorter
            const targetTime = Math.min(seekTo, video.duration / 2);
            video.currentTime = targetTime;
            isSeeking = true;
        };

        video.onseeked = () => {
            if (!isSeeking) return;
            isSeeking = false;

            try {
                const scale = Math.min(1, maxDimension / Math.max(video.videoWidth, video.videoHeight));
                const targetWidth = Math.max(1, Math.round(video.videoWidth * scale));
                const targetHeight = Math.max(1, Math.round(video.videoHeight * scale));

                const canvas = document.createElement("canvas");
                canvas.width = targetWidth;
                canvas.height = targetHeight;
                const ctx = canvas.getContext("2d");

                if (!ctx) {
                    URL.revokeObjectURL(videoUrl);
                    return reject(new Error("Canvas not supported"));
                }

                ctx.drawImage(video, 0, 0, targetWidth, targetHeight);

                canvas.toBlob(
                    (blob) => {
                        URL.revokeObjectURL(videoUrl);
                        if (!blob) return reject(new Error("Failed to create thumbnail"));
                        resolve(blob);
                    },
                    "image/jpeg",
                    quality
                );
            } catch (err) {
                URL.revokeObjectURL(videoUrl);
                reject(err);
            }
        };

        video.onerror = () => {
            URL.revokeObjectURL(videoUrl);
            reject(new Error("Failed to load video"));
        };

        video.src = videoUrl;
    });
};

export const generateVideoThumbnail = (file, seekTo = 1.0, maxDimension = 200, quality = 0.8) => {
  return withTimeout(
    _generateVideoThumbnail(file, seekTo, maxDimension, quality),
    8000,
    "Video thumbnail"
  );
};

const _generatePdfThumbnail = async (file, maxDimension = 200, quality = 0.8) => {
    // Dynamically import pdf.js to avoid bloating the main bundle if not needed immediately
    // Or assume it's available globally/via standard import depending on the project setup
    const pdfjsLib = window.pdfjsLib;

    if (!pdfjsLib) {
        throw new Error("pdfjsLib is not loaded globally.");
    }

    // Set worker src if needed, assuming it's loaded in the app
    // pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument(new Uint8Array(arrayBuffer));
    const pdf = await loadingTask.promise;

    // Get the first page
    const page = await pdf.getPage(1);

    // First, establish the original viewport at scale 1.0 to find dimensions
    let viewport = page.getViewport({ scale: 1.0 });

    // Calculate scale to fit within maxDimension
    const scale = Math.min(1, maxDimension / Math.max(viewport.width, viewport.height));

    // Get the new viewport with the corrected scale
    viewport = page.getViewport({ scale });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
        throw new Error("Canvas not supported");
    }

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    const renderContext = {
        canvasContext: ctx,
        viewport: viewport
    };

    await page.render(renderContext).promise;

    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (!blob) return reject(new Error("Failed to create thumbnail"));
                resolve(blob);
            },
            "image/jpeg",
            quality
        );
    });
};

export const generatePdfThumbnail = (file, maxDimension = 200, quality = 0.8) => {
  return withTimeout(
    _generatePdfThumbnail(file, maxDimension, quality),
    8000,
    "PDF thumbnail"
  );
};
